import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Router } from "@angular/router";
import { OnBoardingService } from "../../services/on-boarding.service";
import { from, zip } from "rxjs";
import { AddStaffService } from "./services/add-staff.service";
import { filter, flatMap, map, toArray } from "rxjs/operators";
import { StaffService } from "src/app/services/staff.service";
import { StaffMemberUpload } from "../../models/StaffMemberUpload";
import { cloneDeep } from "lodash";
import { StaffManagementService } from "src/app/services/staff-management.service";
import { AddStaffPageNumber } from 'src/app/enums/express-add-staff-page-number.enum';

/**
 * AddStaffComponent is a parent component which manages information of staff that child components emit.
 */
@Component({
  selector: "app-onboarding-add-staff",
  templateUrl: "./add-staff.component.html",
  styleUrls: ["./add-staff.component.scss"],
})
export class AddStaffComponent implements OnInit {
  @Input() usedInsideApp = false;
  @Output() nextClicked: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  @Output() uploadFile: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  saveOnDB = false;
  isValidState = false;
  allStaff: StaffMemberUpload[];
  addOrUploadStaff = "manual";
  private localStorageKey = "manualStaff";
  existingStaffData: StaffMemberUpload[];
  isChanged = false;
  renderAddStaff: number;
  renderUploadStaff: number;
  uploadedData = false;
  retrieveInitialData = false;

  public readonly ADD_STAFF_PAGE_NUMBER = AddStaffPageNumber;

  constructor(
    private _router: Router,
    private _onBoardingService: OnBoardingService,
    private _addStaffService: AddStaffService,
    private _staffService: StaffService,
    private _staffManagementService: StaffManagementService,
  ) { }

  ngOnInit(): void {
    this._navigateOnTop();
    this._getStaff().subscribe(resp => {
      this.existingStaffData = resp;
      this.isChanged = this.checkIfChanged();
      this.retrieveInitialData = true;
    });
  }

  get manualAdd$() {
    return this._addStaffService.manuallyAddStaff.asObservable();
  }
  //based on state value when user moves forward can move to other screens of thame step or to move to another route(step=12).
  forwardStateChange() {
    if (!!this.checkIfChanged()) {
      this.callStaffAPI();
    }
    if (!this.usedInsideApp) {
      const nav = this._onBoardingService.onBoardingNavigation.value;
      this._onBoardingService
        .updateStateObservable(
          nav.find((item) => '/' + item.uri === this._router.url.split('?')[0]).next
        )
        .subscribe((resp) => {
          this._onBoardingService.goForward();
        });
    } else {
      this.nextClicked.emit(true);
    }

  }

  moveToManual(manual: boolean) {
    this._addStaffService.manuallyAddStaff.next(manual);
    this.uploadFile.emit(false);
    this.forwardStateChange();
  }

  moveToUpload(file) {
    this._addStaffService.manuallyAddStaff.next(false);
    this._addStaffService.fileToUpload = file;
    this.renderUploadStaff = this.ADD_STAFF_PAGE_NUMBER.fileUploadPage;
    this.uploadFile.emit(true);
    if (this.addOrUploadStaff === 'manual') {
      this.forwardStateChange();
    }
  }


  navigateToNextPage(event) {
    if (event) {
      setTimeout(() => {
        this.forwardStateChange();
      }, 500)
    }
  }

  private _navigateOnTop() {
    setTimeout(() => {
      document.getElementById("onboard-header")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 5);
  }

  getAddStaffType(event) {
    this.addOrUploadStaff = event;
  }

  callStaffAPI() {
    this.isValidState = false;
    const deletedStaffMembers = this.allStaff.filter((i) => i.deleted);
    const modifiedStaffMembers = this.allStaff.filter((i) => i.updated);
    zip(
      from(modifiedStaffMembers).pipe(
        flatMap((e) =>
          this._staffService.generalStaffUpdate({
            staffid: e.id,
            firstname: e.firstname,
            lastname: e.lastname,
            phonenumber: e.phonenumber,
            stafftypeid: e.stafftypeid,
          })
        ),
        toArray()
      ),
      from(deletedStaffMembers).pipe(
        flatMap((e) => this._staffService.removeStaffMember(e.id)),
        toArray()
      ),
    ).subscribe(
      (item) => {
        this._onBoardingService.removePartialInfoInLs(this.localStorageKey);
        this.isChanged = false;
        this._copyDataToExisting();
        this.isValidState = !this.allStaff.find(e => !e.id);
      },
      (error) => {
        this.isValidState = true;
      }
    );
  }

  getUpdatedData(event) {
    this.allStaff = event;
    this.isChanged = this.checkIfChanged();
  }

  checkIfChanged() {
    let changed = false;
    if (this.allStaff && this.allStaff[this.allStaff.length - 1].id) {
      if (this.allStaff.length === this.existingStaffData?.length
        && this.existingStaffData?.filter(e => e.deleted).length === this.allStaff?.filter(e => e.deleted).length
        && this.existingStaffData?.filter(e => !e.deleted).length === this.allStaff?.filter(e => !e.deleted).length
      ) {

        this.existingStaffData?.forEach((item, index) => {
          if (!!this.allStaff[index] && !this._onBoardingService._checkObjects(item, this.allStaff[index])) {
            changed = true;
            return;
          }
        });
      } else {
        changed = true;
      }
    }
    return changed;
  }

  private _copyDataToExisting() {
    this.existingStaffData = cloneDeep(this.allStaff);
  }

  private _getStaff() {
    return this._staffManagementService.getAllStaff({ startDate: '', endDate: '' }).pipe(
      filter(item => !!item.data),
      flatMap(item => item.data),
      filter(item => !!item.active),
      map(item => ({
        id: item.staffId,
        firstname: item.firstName,
        lastname: item.lastName,
        phonenumber: item.phoneNumber,
        stafftypeid: item.staffTypeId,
        updated: false,
        deleted: false
      })), toArray()
    );
  }

  /**
   * Go to manual or csv upload page
   * @param addStaffPage gets page number
   */
  addStaffManualOrCsv(addStaffPage: number): void {
    this.renderAddStaff = addStaffPage;
  }

  /**
   * Go to add staff page
   * @param addPage gets previously selected page number
   */
  goToAddStaffPage(addPage: number): void {
    this.renderUploadStaff = addPage;
  }

}
